// KeyboardEvent imported but not used directly - using React.KeyboardEvent instead
import { useNavigate } from 'react-router-dom'
import { X, Download, Loader2, History, ChevronRight, Calendar, User, Car, Paperclip } from 'lucide-react'
import { Company } from '@services/companyService'
import { FileUpload } from '../../types/fileUpload'
import { CompanySummary } from '../../types/companySummary'
import { useQuery } from 'react-query'
import { workOrderService } from '@services/workOrderService'
import { useAuthStore } from '@store/useAuthStore'
import { useMemo, useState } from 'react'
import React from 'react'
import { WorkOrderStatus } from '../../types/workOrder'

const normalizePlate = (plate?: string) =>
  (plate ?? '').toUpperCase().replace(/\s+/g, '').trim()

interface ClientModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly client: Company | CompanySummary | null
  readonly document?: FileUpload | null
  readonly onDownloadPdf: () => Promise<void>
  readonly isGeneratingPdf: boolean
  readonly isLoadingClient?: boolean
}

// Componente para exibir veículos do cliente e problemas anteriores
function CustomerVehicles({ 
  customerCompanyId, 
  workshopCompanyId,
  onViewVehicleWorkOrders
}: { 
  customerCompanyId: number
  workshopCompanyId: number
  onViewVehicleWorkOrders?: (vehicleId: number, registrationPlate?: string) => void
}) {
  const { data: customerWorkOrders, isLoading } = useQuery(
    ['customerVehicles', customerCompanyId, workshopCompanyId],
    () => workOrderService.getByCustomer(customerCompanyId, workshopCompanyId, 0, 100),
    {
      enabled: !!customerCompanyId && !!workshopCompanyId,
    }
  )

  // Extrair veículos únicos e problemas anteriores das work orders
  const vehiclesWithProblems = useMemo(() => {
    if (!customerWorkOrders || customerWorkOrders.length === 0) return []
    
    const vehicleMap = new Map<string, { 
      vehicleId: number
      vehicleInfo: string
      registrationPlate?: string
      count: number
      latestProblem?: string
      latestDate?: string
    }>()
    
    customerWorkOrders.forEach((wo) => {
      // Usar matrícula como chave principal se disponível, senão usar vehicleInfo
      const key = wo.vehicleRegistrationPlate
        ? `plate:${normalizePlate(wo.vehicleRegistrationPlate)}`
        : wo.vehicleInfo
          ? `info:${wo.vehicleInfo}`
          : `unknown:${wo.vehicleId}`
      
      const problem = wo.description || wo.diagnosis || 'Sem descrição'
      const date = wo.createdAt || ''
      const candidateVehicleId = Number(wo.vehicleId) || 0
      
      if (vehicleMap.has(key)) {
        const existing = vehicleMap.get(key)!
        // Manter o problema mais recente
        const isNewer = date > (existing.latestDate || '')
        vehicleMap.set(key, { 
          ...existing, 
          count: existing.count + 1,
          vehicleInfo: wo.vehicleInfo && wo.vehicleInfo.length > existing.vehicleInfo.length 
            ? wo.vehicleInfo 
            : existing.vehicleInfo,
          latestProblem: isNewer ? problem : existing.latestProblem,
          latestDate: isNewer ? date : existing.latestDate,
          // Se o veículo anterior não tinha ID válido (0) e agora temos, ou se o mais recente tem ID válido
          vehicleId: (existing.vehicleId > 0)
            ? (isNewer && candidateVehicleId > 0 ? candidateVehicleId : existing.vehicleId)
            : (candidateVehicleId > 0 ? candidateVehicleId : existing.vehicleId),
          registrationPlate: existing.registrationPlate || wo.vehicleRegistrationPlate,
        })
      } else {
        vehicleMap.set(key, {
          vehicleId: candidateVehicleId,
          vehicleInfo: wo.vehicleInfo || 'Veículo não especificado',
          registrationPlate: wo.vehicleRegistrationPlate,
          count: 1,
          latestProblem: problem,
          latestDate: date
        })
      }
    })
    
    return Array.from(vehicleMap.values()).sort((a, b) => {
      // Ordenar por data mais recente primeiro
      if (a.latestDate && b.latestDate) {
        return b.latestDate.localeCompare(a.latestDate)
      }
      if (a.latestDate) return -1
      if (b.latestDate) return 1
      return b.count - a.count
    })
  }, [customerWorkOrders])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-secondary-400" />
      </div>
    )
  }

  if (vehiclesWithProblems.length === 0) {
    return (
      <div className="py-4">
        <p className="text-sm text-secondary-500">Nenhum veículo registado para este cliente.</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {vehiclesWithProblems.map((vehicle, index) => (
        <div
          key={index}
          className="rounded-lg border border-secondary-200 bg-white p-4 hover:border-primary-300 hover:shadow-sm transition-all"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {vehicle.registrationPlate ? (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-secondary-900">{vehicle.registrationPlate}</span>
                    {vehicle.count > 1 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary-100 text-secondary-700">
                        {vehicle.count} folhas
                      </span>
                    )}
                  </div>
                  {vehicle.vehicleInfo && (
                    <p className="text-xs text-secondary-500 mt-1">
                      {vehicle.vehicleInfo}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-secondary-900">{vehicle.vehicleInfo}</span>
                    {vehicle.count > 1 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary-100 text-secondary-700">
                        {vehicle.count} folhas
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-secondary-500 mt-1">
                    Matrícula não disponível
                  </p>
                </>
              )}
              {vehicle.latestProblem && (
                <p className="text-xs text-secondary-600 mt-2 line-clamp-2">
                  <span className="font-medium">Último problema:</span> {vehicle.latestProblem}
                </p>
              )}
            </div>
            {onViewVehicleWorkOrders && vehicle.count > 0 && vehicle.vehicleId > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  console.log('Button clicked for vehicle:', { vehicleId: vehicle.vehicleId, plate: vehicle.registrationPlate, count: vehicle.count })
                  onViewVehicleWorkOrders(vehicle.vehicleId, vehicle.registrationPlate)
                }}
                className="ml-2 p-2 rounded-lg hover:bg-secondary-100 transition-colors flex-shrink-0"
                title={`Ver ${vehicle.count} folha${vehicle.count > 1 ? 's' : ''} de obra deste veículo`}
                aria-label="Ver folhas de obra"
              >
                <History className="h-4 w-4 text-[#17253C] hover:text-[#1f2f4a]" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export function ClientModal({
  isOpen,
  onClose,
  client,
  document,
  onDownloadPdf,
  isGeneratingPdf,
  isLoadingClient = false,
}: Readonly<ClientModalProps>) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const workshopCompanyId = user?.workshopCompanyId
  const [showWorkOrdersModal, setShowWorkOrdersModal] = useState(false)
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null)
  const [selectedVehiclePlate, setSelectedVehiclePlate] = useState<string | undefined>(undefined)

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 md:p-4 backdrop-blur-sm"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-modal-title"
      tabIndex={-1}
    >
      <div className="relative w-full max-w-[calc(100vw-1.5rem)] sm:max-w-md rounded-2xl md:rounded-3xl border border-secondary-200 bg-white shadow-2xl max-h-[85vh] md:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-secondary-200 px-4 py-3 md:px-6 md:py-4 flex-shrink-0">
          <h2 id="client-modal-title" className="text-base md:text-lg font-semibold text-secondary-900">Informações do Cliente</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-secondary-400 transition-colors hover:bg-secondary-100 hover:text-secondary-600"
            aria-label="Fechar modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-4 md:px-6 md:py-6 overflow-y-auto flex-1 min-h-0">
          {client ? (
            <div className="space-y-3 md:space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary-400">
                  Nome
                </label>
                <p className="mt-1 text-sm md:text-base font-semibold text-secondary-900">{client.name}</p>
              </div>

              {(client as any).nif && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary-400">
                    NIF
                  </label>
                  <p className="mt-1 text-sm text-secondary-700">{(client as any).nif}</p>
                </div>
              )}

              {(client as any).phone && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary-400">
                    Telemóvel
                  </label>
                  <a
                    href={`tel:${(client as any).phone}`}
                    className="mt-1 block text-sm text-secondary-700 hover:text-primary-600 hover:underline transition-colors"
                  >
                    {(client as any).phone}
                  </a>
                </div>
              )}

              {(client as any).email && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary-400">
                    Email
                  </label>
                  <a
                    href={`mailto:${(client as any).email}`}
                    className="mt-1 block text-sm text-secondary-700 hover:text-primary-600 hover:underline transition-colors"
                  >
                    {(client as any).email}
                  </a>
                </div>
              )}

              {client.address && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary-400">
                    Endereço
                  </label>
                  <p className="mt-1 text-sm text-secondary-700">{client.address}</p>
                </div>
              )}

              {/* Carros e Problemas Anteriores */}
              {client.id && workshopCompanyId && (
                <div className="pt-4 border-t border-secondary-200">
                  <label className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary-400 mb-3 block">
                    Carros e Problemas Anteriores
                  </label>
                  <CustomerVehicles
                    customerCompanyId={client.id}
                    workshopCompanyId={workshopCompanyId}
                    onViewVehicleWorkOrders={(vehicleId, registrationPlate) => {
                      console.log('Opening work orders modal for vehicle:', { vehicleId, registrationPlate })
                      if (vehicleId && vehicleId > 0) {
                        setSelectedVehicleId(vehicleId)
                        setSelectedVehiclePlate(registrationPlate)
                        setShowWorkOrdersModal(true)
                      } else {
                        console.warn('Invalid vehicleId:', vehicleId)
                      }
                    }}
                  />
                </div>
              )}

              {document && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary-400">
                    Documento
                  </label>
                  <p className="mt-1 text-sm text-secondary-700">
                    {document.description || document.originalFilename}
                  </p>
                </div>
              )}
            </div>
          ) : isLoadingClient ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-secondary-400" />
            </div>
          ) : (
            <div className="py-8 text-center text-secondary-500">
              <p>Nenhuma informação disponível</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-secondary-200 px-4 py-3 md:px-6 md:py-4 flex-shrink-0">
          <button
            onClick={onDownloadPdf}
            disabled={isGeneratingPdf || !client}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:from-primary-600 hover:to-primary-700 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isGeneratingPdf ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                A gerar PDF...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Transferir PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Modal de Folhas de Obra por Veículo */}
      {showWorkOrdersModal && selectedVehicleId && selectedVehicleId > 0 && workshopCompanyId && (
        <WorkOrdersHistoryModal
          isOpen={showWorkOrdersModal}
          onClose={() => {
            setShowWorkOrdersModal(false)
            setSelectedVehicleId(null)
            setSelectedVehiclePlate(undefined)
          }}
          customerCompanyId={client?.id ? Number(client.id) : undefined}
          vehicleId={selectedVehicleId}
          vehiclePlate={selectedVehiclePlate}
          workshopCompanyId={workshopCompanyId}
          onNavigateToWorkOrder={(woId) => {
            setShowWorkOrdersModal(false)
            setSelectedVehicleId(null)
            setSelectedVehiclePlate(undefined)
            navigate(`/work-orders/${woId}#attachments`)
          }}
        />
      )}
    </div>
  )
}


// Componente para exibir modal com folhas de obra de um veículo específico
function WorkOrdersHistoryModal({
  isOpen,
  onClose,
  customerCompanyId,
  vehicleId,
  vehiclePlate,
  workshopCompanyId,
  onNavigateToWorkOrder,
}: {
  isOpen: boolean
  onClose: () => void
  customerCompanyId?: number
  vehicleId: number
  vehiclePlate?: string
  workshopCompanyId: number
  onNavigateToWorkOrder?: (workOrderId: number) => void
}) {
  const { data: workOrders, isLoading, isError, error, refetch } = useQuery(
    ['vehicleWorkOrdersHistory', customerCompanyId, vehicleId, vehiclePlate, workshopCompanyId],
    async () => {
      if (!vehicleId || vehicleId === 0) {
        return []
      }

      // Primary: fetch by vehicle. In some deployments this endpoint returns empty even when
      // getByCustomer shows work orders for the same plate/client, so we add a safe fallback.
      const byVehicle = await workOrderService.getByVehicle(vehicleId, 0, 100, workshopCompanyId ?? undefined)
      if (byVehicle && byVehicle.length > 0) return byVehicle

      // Fallback: fetch by customer and filter down to this vehicle (by id and/or plate).
      if (!customerCompanyId) return byVehicle || []
      const byCustomer = await workOrderService.getByCustomer(customerCompanyId, workshopCompanyId, 0, 200)
      const normalizedTargetPlate = normalizePlate(vehiclePlate)
      const filtered = (byCustomer || []).filter((wo) => {
        if (wo.vehicleId === vehicleId) return true
        if (normalizedTargetPlate && wo.vehicleRegistrationPlate && normalizePlate(wo.vehicleRegistrationPlate) === normalizedTargetPlate) return true
        return false
      })
      return filtered
    },
    {
      enabled: isOpen && !!vehicleId && vehicleId > 0,
      staleTime: 0,
      cacheTime: 0,
      retry: 1,
    }
  )

  // Refetch quando o modal abre
  React.useEffect(() => {
    if (isOpen && vehicleId && vehicleId > 0) {
      console.log('Modal opened, refetching work orders for vehicle:', vehicleId)
      refetch()
    }
  }, [isOpen, vehicleId, refetch])

  const [expandedWorkOrderId, setExpandedWorkOrderId] = useState<number | null>(null)

  // Ordenar da mais recente para a mais antiga (todas as folhas, não apenas 3)
  const sortedWorkOrders = useMemo(() => {
    if (!workOrders || workOrders.length === 0) return []
    return [...workOrders]
      .sort((a, b) => {
        const dateA = a.createdAt || ''
        const dateB = b.createdAt || ''
        return dateB.localeCompare(dateA) // Mais recente primeiro
      })
  }, [workOrders])

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return new Intl.DateTimeFormat('pt-PT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    } catch {
      return dateString
    }
  }

  const formatStatus = (status: WorkOrderStatus): string => {
    const statusMap: Record<WorkOrderStatus, string> = {
      [WorkOrderStatus.SCHEDULED]: 'Agendado',
      [WorkOrderStatus.PENDING]: 'Pendente',
      [WorkOrderStatus.DRAFT]: 'Rascunho',
      [WorkOrderStatus.IN_PROGRESS]: 'Em Andamento',
      [WorkOrderStatus.WAITING_PARTS]: 'Aguarda Peças',
      [WorkOrderStatus.WAITING_APPROVAL]: 'Aguarda Aprovação',
      [WorkOrderStatus.RESOLVED]: 'Resolvida',
      [WorkOrderStatus.COMPLETED]: 'Concluída',
      [WorkOrderStatus.CANCELLED]: 'Cancelada',
      [WorkOrderStatus.INVOICED]: 'Faturada',
    }
    return statusMap[status] || status
  }

  const computeWorkOrderCosts = (wo: any) => {
    const serviceItems = Array.isArray(wo?.serviceItems) ? wo.serviceItems : []
    const pieceItems = Array.isArray(wo?.pieceItems) ? wo.pieceItems : []

    const computedLabor = serviceItems.reduce((sum: number, item: any) => {
      const subtotal = typeof item?.subtotal === 'number'
        ? item.subtotal
        : (Number(item?.hours) || 0) * (Number(item?.hourlyRate) || 0)
      return sum + (Number.isFinite(subtotal) ? subtotal : 0)
    }, 0)

    const computedParts = pieceItems.reduce((sum: number, item: any) => {
      const subtotal = typeof item?.subtotal === 'number'
        ? item.subtotal
        : (Number(item?.quantity) || 0) * (Number(item?.unitPrice) || 0)
      return sum + (Number.isFinite(subtotal) ? subtotal : 0)
    }, 0)

    const laborCost = (Number(wo?.laborCost) || 0) > 0 ? Number(wo?.laborCost) : computedLabor
    const partsCost = (Number(wo?.partsCost) || 0) > 0 ? Number(wo?.partsCost) : computedParts
    const budgetCosts = Array.isArray(wo?.budgetCosts) ? wo.budgetCosts : []
    const extra = budgetCosts.length > 0
      ? budgetCosts.reduce((sum: number, cost: any) => sum + (Number(cost?.amount) || 0), 0)
      : (Number(wo?.extraAmount) || 0)
    const finalAmount = laborCost + partsCost + extra

    return { laborCost, partsCost, extra, finalAmount }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Modal principal com lista compactada */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
        <div className="relative w-full max-w-2xl rounded-3xl border border-secondary-200 bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between bg-[#17253C] text-white px-6 py-4">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5" />
              <div>
                <h2 className="text-lg font-semibold">Folhas de Obra</h2>
                {vehiclePlate && (
                  <p className="text-xs text-white/80 mt-0.5">Matrícula: {vehiclePlate}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Fechar modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-secondary-400" />
              </div>
            ) : isError ? (
              <div className="py-8 text-center text-secondary-500">
                <p className="font-semibold text-secondary-700">Não foi possível carregar as folhas de obra.</p>
                <p className="text-xs mt-2">
                  {(error as any)?.message || 'Erro desconhecido'}
                </p>
              </div>
            ) : sortedWorkOrders.length === 0 ? (
              <div className="py-8 text-center text-secondary-500">
                <p>Nenhuma folha de obra encontrada para este veículo.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedWorkOrders.map((wo) => {
                  const isExpanded = expandedWorkOrderId === wo.id
                  return (
                    <div
                      key={wo.id}
                      className="rounded-lg border border-secondary-200 bg-white overflow-hidden transition-all"
                    >
                      <button
                        onClick={() => setExpandedWorkOrderId(isExpanded ? null : wo.id)}
                        className="w-full text-left p-4 hover:bg-secondary-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold text-secondary-900">{wo.workOrderNumber}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                wo.status === WorkOrderStatus.COMPLETED ? 'bg-green-100 text-green-800' :
                                wo.status === WorkOrderStatus.INVOICED ? 'bg-blue-100 text-blue-800' :
                                wo.status === WorkOrderStatus.CANCELLED ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {formatStatus(wo.status)}
                              </span>
                            </div>
                            {wo.description && (
                              <p className="text-xs text-secondary-600 mt-1 line-clamp-2">{wo.description}</p>
                            )}
                            {wo.createdAt && (
                              <p className="text-xs text-secondary-400 mt-1 flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(wo.createdAt)}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {onNavigateToWorkOrder && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onNavigateToWorkOrder(wo.id)
                                  onClose()
                                }}
                                className="p-2 rounded-lg hover:bg-secondary-100 text-secondary-500 hover:text-primary-600 transition-colors"
                                title="Ver anexos desta folha de obra"
                                aria-label="Ver anexos"
                              >
                                <Paperclip className="h-4 w-4" />
                              </button>
                            )}
                            <ChevronRight 
                              className={`h-4 w-4 text-secondary-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                            />
                          </div>
                        </div>
                      </button>
                      
                      {/* Detalhes expandidos */}
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-secondary-100 bg-secondary-50">
                          <div className="pt-4 space-y-4">
                            {/* Informações Gerais */}
                            <div className="rounded-lg border border-secondary-200 bg-white p-3">
                              <h3 className="text-xs font-semibold text-secondary-900 mb-2">Informações Gerais</h3>
                              <div className="grid gap-2 sm:grid-cols-2 text-xs">
                                {wo.customerCompanyName && (
                                  <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-secondary-400 mb-0.5">
                                      Cliente
                                    </p>
                                    <p className="text-secondary-700 flex items-center gap-1">
                                      <User className="h-3 w-3" />
                                      {wo.customerCompanyName}
                                    </p>
                                  </div>
                                )}
                                {wo.vehicleInfo && (
                                  <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-secondary-400 mb-0.5">
                                      Veículo
                                    </p>
                                    <p className="text-secondary-700 flex items-center gap-1">
                                      <Car className="h-3 w-3" />
                                      {wo.vehicleInfo}
                                    </p>
                                    {wo.vehicleRegistrationPlate && (
                                      <p className="text-[10px] text-secondary-500 mt-0.5">
                                        Matrícula: {wo.vehicleRegistrationPlate}
                                      </p>
                                    )}
                                  </div>
                                )}
                                {wo.createdAt && (
                                  <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-secondary-400 mb-0.5">
                                      Data de Criação
                                    </p>
                                    <p className="text-secondary-700 flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {formatDate(wo.createdAt)}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Descrição */}
                            {wo.description && (
                              <div className="rounded-lg border border-secondary-200 bg-white p-3">
                                <h3 className="text-xs font-semibold text-secondary-900 mb-1">Descrição</h3>
                                <p className="text-xs text-secondary-700 whitespace-pre-wrap">{wo.description}</p>
                              </div>
                            )}

                            {/* Notas do cliente */}
                            {wo.diagnosis && (
                              <div className="rounded-lg border border-secondary-200 bg-white p-3">
                                <h3 className="text-xs font-semibold text-secondary-900 mb-1">Notas do cliente</h3>
                                <p className="text-xs text-secondary-700 whitespace-pre-wrap">{wo.diagnosis}</p>
                              </div>
                            )}

                            {/* Observações */}
                            {wo.observations && (
                              <div className="rounded-lg border border-secondary-200 bg-white p-3">
                                <h3 className="text-xs font-semibold text-secondary-900 mb-1">Observações</h3>
                                <p className="text-xs text-secondary-700 whitespace-pre-wrap">{wo.observations}</p>
                              </div>
                            )}

                            {/* Custos */}
                            <div className="rounded-lg border border-secondary-200 bg-white p-3">
                              <h3 className="text-xs font-semibold text-secondary-900 mb-2">Custos</h3>
                              <div className="space-y-1.5 text-xs">
                                {(() => {
                                  const costs = computeWorkOrderCosts(wo)
                                  return (
                                    <>
                                      <div className="flex justify-between">
                                        <span className="text-secondary-600">Mão de Obra:</span>
                                        <span className="font-semibold text-secondary-900">{costs.laborCost.toFixed(2)} €</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-secondary-600">Peças:</span>
                                        <span className="font-semibold text-secondary-900">{costs.partsCost.toFixed(2)} €</span>
                                      </div>
                                      {costs.extra !== 0 && (
                                        <div className="flex justify-between">
                                          <span className="text-secondary-600">Extra:</span>
                                          <span className="font-semibold text-secondary-900">{costs.extra.toFixed(2)} €</span>
                                        </div>
                                      )}
                                      <div className="flex justify-between border-t border-secondary-200 pt-1.5">
                                        <span className="font-semibold text-secondary-900">Total:</span>
                                        <span className="font-bold text-primary-600">{costs.finalAmount.toFixed(2)} €</span>
                                      </div>
                                    </>
                                  )
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>

    </>
  )
}

